import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Camera } from '@babylonjs/core/Cameras/camera';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { Scene } from '@babylonjs/core/scene';
import { WeaponId } from '@fisura/sim';

const BASE = new Vector3(0.24, -0.22, 0.5); // hip position: bottom-right of the eye
const ADS_POS = new Vector3(0, -0.13, 0.42); // raised to eye-center while aiming

interface GunSpec {
  bodyLen: number;
  bodyH: number;
  barrelLen: number;
  barrelR: number;
  magH: number;
  stock: boolean;
}

const SPECS: Record<number, GunSpec> = {
  [WeaponId.SMG]: { bodyLen: 0.2, bodyH: 0.07, barrelLen: 0.14, barrelR: 0.014, magH: 0.12, stock: false },
  [WeaponId.Shotgun]: { bodyLen: 0.26, bodyH: 0.08, barrelLen: 0.3, barrelR: 0.02, magH: 0.05, stock: true },
  [WeaponId.Rifle]: { bodyLen: 0.28, bodyH: 0.075, barrelLen: 0.34, barrelR: 0.013, magH: 0.14, stock: true },
  // Rocket launcher: one fat tube, no mag.
  [WeaponId.Rocket]: { bodyLen: 0.18, bodyH: 0.09, barrelLen: 0.55, barrelR: 0.055, magH: 0.04, stock: true },
};

/**
 * Build a simple but readable gun silhouette (receiver, barrel, grip, mag,
 * sight, optional stock) parented to `parent`, pointing +Z. Returns the muzzle
 * tip node (for the flash) so callers can attach effects.
 */
export function buildGunMesh(
  scene: Scene,
  parent: TransformNode,
  weaponId: number,
  metal: StandardMaterial,
  grip: StandardMaterial,
  namePrefix: string,
): TransformNode {
  const s = SPECS[weaponId] ?? SPECS[WeaponId.SMG]!;
  const part = (name: string, w: number, h: number, d: number, mat: StandardMaterial): Mesh => {
    const m = MeshBuilder.CreateBox(`${namePrefix}-${name}`, { width: w, height: h, depth: d }, scene);
    m.material = mat;
    m.parent = parent;
    m.isPickable = false;
    return m;
  };

  // Receiver/body centred forward of origin.
  const body = part('body', 0.045, s.bodyH, s.bodyLen, metal);
  body.position.set(0, 0, s.bodyLen / 2);
  // Barrel.
  const barrel = MeshBuilder.CreateCylinder(
    `${namePrefix}-barrel`,
    { height: s.barrelLen, diameter: s.barrelR * 2, tessellation: 10 },
    scene,
  );
  barrel.material = metal;
  barrel.parent = parent;
  barrel.rotation.x = Math.PI / 2;
  barrel.position.set(0, s.bodyH * 0.18, s.bodyLen + s.barrelLen / 2);
  barrel.isPickable = false;
  // Grip.
  const g = part('grip', 0.035, 0.09, 0.045, grip);
  g.position.set(0, -s.bodyH / 2 - 0.04, s.bodyLen * 0.25);
  g.rotation.x = -0.25;
  // Magazine.
  const mag = part('mag', 0.032, s.magH, 0.05, grip);
  mag.position.set(0, -s.bodyH / 2 - s.magH / 2 + 0.01, s.bodyLen * 0.6);
  mag.rotation.x = 0.12;
  // Sight nub.
  const sight = part('sight', 0.012, 0.025, 0.03, metal);
  sight.position.set(0, s.bodyH / 2 + 0.014, s.bodyLen * 0.55);
  // Stock.
  if (s.stock) {
    const st = part('stock', 0.035, 0.06, 0.12, grip);
    st.position.set(0, -0.005, -0.06);
  }

  // Muzzle tip anchor (for flash placement).
  const muzzle = new TransformNode(`${namePrefix}-muzzle`, scene);
  muzzle.parent = parent;
  muzzle.position.set(0, s.bodyH * 0.18, s.bodyLen + s.barrelLen);
  return muzzle;
}

/**
 * First-person weapon: a readable gun mesh parented to the camera with sway,
 * bob, recoil kick and a brief muzzle flash on fire.
 */
export class ViewModel {
  private readonly holder: TransformNode;
  private gunRoot: TransformNode | null = null;
  private flashMesh: Mesh | null = null;
  private readonly metal: StandardMaterial;
  private readonly grip: StandardMaterial;
  private readonly flashMat: StandardMaterial;
  private weaponId = -1;
  private t = 0;
  private recoil = 0;
  private flashUntil = 0;
  private adsBlend = 0; // 0 = hip, 1 = aiming down sights

  constructor(
    private readonly scene: Scene,
    camera: Camera,
  ) {
    this.holder = new TransformNode('viewmodel', scene);
    this.holder.parent = camera;
    this.holder.position.copyFrom(BASE);

    this.metal = new StandardMaterial('vm-metal', scene);
    this.metal.diffuseColor = new Color3(0.16, 0.17, 0.2);
    this.metal.specularColor = new Color3(0.35, 0.35, 0.4);
    this.grip = new StandardMaterial('vm-grip', scene);
    this.grip.diffuseColor = new Color3(0.1, 0.09, 0.08);
    this.grip.specularColor = new Color3(0.08, 0.08, 0.08);
    this.flashMat = new StandardMaterial('vm-flash', scene);
    this.flashMat.disableLighting = true;
    this.flashMat.emissiveColor = new Color3(1, 0.85, 0.4);

    this.setWeapon(WeaponId.SMG);
  }

  setWeapon(id: number): void {
    if (id === this.weaponId) return;
    this.weaponId = id;
    this.gunRoot?.dispose(false, true);
    this.flashMesh?.dispose();
    this.gunRoot = new TransformNode('vm-gun', this.scene);
    this.gunRoot.parent = this.holder;
    const muzzle = buildGunMesh(this.scene, this.gunRoot, id, this.metal, this.grip, 'vm');
    // Render the whole gun on top of the world so it never clips into walls.
    for (const m of this.gunRoot.getChildMeshes(false)) m.renderingGroupId = 1;
    // Muzzle flash: a small bright cross of two quads at the tip.
    const f = MeshBuilder.CreatePlane('vm-flashq', { size: 0.12 }, this.scene);
    f.material = this.flashMat;
    f.parent = muzzle;
    f.renderingGroupId = 1;
    f.isPickable = false;
    f.billboardMode = 7; // BILLBOARDMODE_ALL — always face the camera
    f.setEnabled(false);
    this.flashMesh = f;
  }

  kick(): void {
    this.recoil = Math.min(0.06, this.recoil + 0.025);
    this.flashUntil = performance.now() + 45;
    this.flashMesh?.setEnabled(true);
    if (this.flashMesh) this.flashMesh.rotation.z = Math.random() * Math.PI;
  }

  update(dt: number, moving: boolean, aiming = false): void {
    this.t += dt * (moving ? 9 : 2.5);
    // ADS blends to the raised pose and damps the bob.
    const target = aiming ? 1 : 0;
    this.adsBlend += (target - this.adsBlend) * Math.min(1, dt * 14);
    const amp = (moving ? 0.012 : 0.004) * (1 - this.adsBlend * 0.8);
    const bobX = Math.cos(this.t) * amp;
    const bobY = Math.abs(Math.sin(this.t)) * amp;
    this.recoil = Math.max(0, this.recoil - dt * 0.4);
    const a = this.adsBlend;
    this.holder.position.set(
      BASE.x + (ADS_POS.x - BASE.x) * a + bobX,
      BASE.y + (ADS_POS.y - BASE.y) * a + bobY + this.recoil * 0.3,
      BASE.z + (ADS_POS.z - BASE.z) * a - this.recoil,
    );
    this.holder.rotation.x = -this.recoil * 2.2;
    if (this.flashUntil && performance.now() >= this.flashUntil) {
      this.flashMesh?.setEnabled(false);
      this.flashUntil = 0;
    }
  }
}
